import { useState } from "react";
import type { TrashedBoard } from "../workspace/workspace_scan";
import { Modal } from "./modal";

export interface TrashDialogProps {
  readonly boards: readonly TrashedBoard[];
  readonly onClose: () => void;
  readonly onEmpty: () => Promise<void>;
  readonly onRestore: (board: TrashedBoard) => Promise<void>;
}

/** Renders recoverable boards and a separately confirmed permanent-empty action. */
export function TrashDialog({ boards, onClose, onEmpty, onRestore }: TrashDialogProps) {
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Restores one board while keeping all other trash entries available. */
  const restore = async (board: TrashedBoard): Promise<void> => {
    setBusyPath(board.path.join("/"));
    setError(null);
    try {
      await onRestore(board);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The board could not be restored.");
    } finally {
      setBusyPath(null);
    }
  };

  /** Permanently empties reserved trash only after the second explicit confirmation. */
  const empty = async (): Promise<void> => {
    setBusyPath("trash");
    setError(null);
    try {
      await onEmpty();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Trash could not be emptied safely.");
    } finally {
      setBusyPath(null);
    }
  };

  return (
    <Modal title="Shotboard Trash" titleId="trash-title" onClose={onClose}>
      <div className="trash-dialog">
        <p className="modal__lede">
          Boards remain recoverable here until Trash is explicitly emptied.
        </p>
        {boards.length === 0 ? (
          <p className="trash-dialog__empty">Trash is empty.</p>
        ) : (
          <ul>
            {boards.map((board) => {
              const busy = busyPath === board.path.join("/");
              return (
                <li key={board.path.join("/")}>
                  <span>
                    <strong>{board.name}</strong>
                    <small>From {board.trashMetadata.originalPath.join(" / ")}</small>
                  </span>
                  <button
                    type="button"
                    disabled={busyPath !== null}
                    onClick={() => void restore(board)}
                  >
                    {busy ? "Restoring…" : `Restore ${board.name}`}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <footer className="modal__actions">
          {confirmEmpty ? (
            <>
              <button
                type="button"
                className="secondary-action"
                disabled={busyPath !== null}
                onClick={() => setConfirmEmpty(false)}
              >
                Keep Boards
              </button>
              <button
                type="button"
                className="danger-action"
                disabled={busyPath !== null}
                onClick={() => void empty()}
              >
                {busyPath === "trash" ? "Emptying…" : "Permanently Empty Trash"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="secondary-action"
              disabled={boards.length === 0 || busyPath !== null}
              onClick={() => setConfirmEmpty(true)}
            >
              Empty Trash…
            </button>
          )}
          <button
            type="button"
            className="primary-action"
            disabled={busyPath !== null}
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </Modal>
  );
}
